import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FileText,
  Plus,
  Search, 
  Filter, 
  Download, 
  Trash2, 
  Calendar, 
  Eye,
  CreditCard,
  Users,
  Truck
} from 'lucide-react';
import Card, { CardHeader, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Select from '../components/ui/Select';
import Input from '../components/ui/Input';
import { useAppStore } from '../store';
import { formatCurrency } from '../utils/formatters';
import { formatArgentinaDate } from '../utils/dateUtils';
import { generateInvoicePDF } from '../utils/pdfGenerator';
import InvoiceModal from '../components/modals/InvoiceModal';
import PaymentModal from '../components/modals/PaymentModal';
import ConfirmModal from '../components/modals/ConfirmModal';
import ErrorModal from '../components/modals/ErrorModal';

const Invoices: React.FC = () => {
  const navigate = useNavigate();
  const { invoices, clients, suppliers, deleteInvoice } = useAppStore();
  
  // State for filters
  const [period, setPeriod] = useState('month');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEntityType, setSelectedEntityType] = useState<'all' | 'client' | 'supplier'>('all');
  const [selectedInvoiceType, setSelectedInvoiceType] = useState<'all' | 'A' | 'B' | 'C' | 'X'>('all');
  
  // State for delete confirmation
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState<string | null>(null);
  
  // State for error handling
  const [error, setError] = useState<string | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  
  // State for invoice creation wizard
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [selectedEntityId, setSelectedEntityId] = useState<string | null>(null);
  const [selectedEntityName, setSelectedEntityName] = useState<string>(''); 
  const [entitySearchQuery, setEntitySearchQuery] = useState<string>('');
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [createdInvoiceId, setCreatedInvoiceId] = useState<string | null>(null);
  
  // Set default date range when period changes
  React.useEffect(() => {
    const now = new Date();
    let start = new Date();
    
    switch (period) {
      case 'today':
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        break;
      case '7days':
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6);
        break;
      case 'month':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'quarter':
        start = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
        break;
      case 'year':
        start = new Date(now.getFullYear(), 0, 1);
        break;
      case 'custom':
        // Don't change dates for custom range
        return;
      default:
        // 'all' - don't set date filters
        setStartDate('');
        setEndDate('');
        return;
    }
    
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(now.toISOString().split('T')[0]);
  }, [period]);
  
  // Filter invoices based on criteria
  const filteredInvoices = useMemo(() => {
    return invoices.filter(invoice => {
      // Filter by entity type
      if (selectedEntityType !== 'all' && invoice.entity_type !== selectedEntityType) {
        return false;
      }
      
      // Filter by invoice type
      if (selectedInvoiceType !== 'all' && invoice.type !== selectedInvoiceType) {
        return false;
      }
      
      // Filter by date range
      if (startDate && endDate) {
        const invoiceDate = new Date(invoice.date);
        const start = new Date(startDate);
        const end = new Date(endDate);
        end.setHours(23, 59, 59); // Include the entire end day
        
        if (invoiceDate < start || invoiceDate > end) {
          return false;
        }
      }
      
      // Filter by search query
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const number = invoice.number.toLowerCase();
        
        // Get entity name
        let entityName = '';
        if (invoice.entity_type === 'client') {
          const client = clients.find(c => c.id === invoice.entity_id);
          entityName = client?.name.toLowerCase() || '';
        } else {
          const supplier = suppliers.find(s => s.id === invoice.entity_id);
          entityName = supplier?.name.toLowerCase() || '';
        }
        
        // Check if query matches invoice number or entity name
        if (!number.includes(query) && !entityName.includes(query)) {
          return false;
        }
      }
      
      return true;
    });
  }, [invoices, selectedEntityType, selectedInvoiceType, startDate, endDate, searchQuery, clients, suppliers]);
  
  // Sort invoices by date (most recent first)
  const sortedInvoices = useMemo(() => {
    return [...filteredInvoices].sort((a, b) => {
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    });
  }, [filteredInvoices]);
  
  // Get entity name by ID and type
  const getEntityName = (entityId: string, entityType: 'client' | 'supplier'): string => {
    if (entityType === 'client') {
      const client = clients.find(c => c.id === entityId);
      return client?.name || 'Cliente desconocido';
    } else {
      const supplier = suppliers.find(s => s.id === entityId);
      return supplier?.name || 'Proveedor desconocido';
    }
  };
  
  // Handle invoice deletion
  const handleDeleteInvoice = (id: string) => {
    setInvoiceToDelete(id);
    setDeleteModalOpen(true);
  };
  
  // Confirm invoice deletion
  const confirmDeleteInvoice = async () => {
    if (!invoiceToDelete) return;
    
    try {
      await deleteInvoice(invoiceToDelete);
      setInvoiceToDelete(null);
    } catch (error: any) {
      console.error('Error deleting invoice:', error);
      setError(error.message || 'Error al eliminar la factura');
      setShowErrorModal(true);
    }
  };
  
  // Generate PDF for an invoice
  const handleGeneratePDF = (invoice: any) => {
    const entityName = getEntityName(invoice.entity_id, invoice.entity_type);
    generateInvoicePDF(invoice, entityName);
  };
  
  // View invoice details
  const handleViewInvoice = (invoice: any) => {
    if (invoice.entity_type === 'client') {
      navigate(`/clientes/${invoice.entity_id}`);
    } else {
      navigate(`/proveedores/${invoice.entity_id}`);
    }
  };
  
  // Reset filters
  const resetFilters = () => {
    setPeriod('all');
    setStartDate('');
    setEndDate('');
    setSearchQuery('');
    setSelectedEntityType('all');
    setSelectedInvoiceType('all');
  };
  
  // Handle wizard steps
  const handleStartWizard = () => {
    setIsWizardOpen(true);
    setWizardStep(1);
    setEntitySearchQuery('');
    setSelectedEntityId(null);
    setSelectedEntityName('');
  };
  
  const handleSelectEntityType = (type: 'client' | 'supplier') => {
    setSelectedEntityType(type);
    setWizardStep(2);
    setEntitySearchQuery('');
  };
  
  const handleSelectEntity = (id: string, name: string) => {
    setSelectedEntityId(id);
    setSelectedEntityName(name);
    setIsWizardOpen(false);
    setIsInvoiceModalOpen(true);
  };
  
  const handleInvoiceCreated = (invoiceId: string) => {
    setCreatedInvoiceId(invoiceId);
    setIsInvoiceModalOpen(false);
    
    // Ask if user wants to register a payment
    if (window.confirm('¿Desea registrar un pago para esta factura?')) {
      setIsPaymentModalOpen(true);
    }
  };
  
  const handlePaymentModalClose = () => {
    setIsPaymentModalOpen(false);
    setCreatedInvoiceId(null);
  };
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white flex items-center">
          <FileText className="mr-2 h-6 w-6 text-indigo-600 dark:text-indigo-400" />
          Facturas
        </h2>
        <div className="flex gap-2">
          <Button
            variant="primary"
            icon={<Plus className="h-5 w-5" />}
            onClick={handleStartWizard}
          >
            Nueva Factura
          </Button>
          <Button
            variant="outline"
            icon={<Filter className="h-4 w-4" />}
            onClick={resetFilters}
          >
            Limpiar Filtros
          </Button>
        </div>
      </div>
      
      {/* Invoice Creation Wizard */}
      {isWizardOpen && (
        <Card>
          <CardHeader title={wizardStep === 1 ? "Crear Nueva Factura" : `Seleccionar ${selectedEntityType === 'client' ? 'Cliente' : 'Proveedor'}`} />
          <CardContent className="max-w-2xl mx-auto">
            {wizardStep === 1 && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Button
                    variant="outline"
                    className="h-24 flex flex-col items-center justify-center"
                    onClick={() => handleSelectEntityType('client')}
                  >
                    <Users className="h-8 w-8 mb-1 text-indigo-600 dark:text-indigo-400" />
                    <span className="text-base font-medium">Factura a Cliente</span>
                  </Button>
                  <Button
                    variant="outline"
                    className="h-24 flex flex-col items-center justify-center"
                    onClick={() => handleSelectEntityType('supplier')}
                  >
                    <Truck className="h-8 w-8 mb-1 text-indigo-600 dark:text-indigo-400" />
                    <span className="text-base font-medium">Factura de Proveedor</span>
                  </Button>
                </div>
                <div className="flex justify-end mt-4">
                  <Button
                    variant="outline"
                    onClick={() => setIsWizardOpen(false)}
                  >
                    Cancelar
                  </Button>
                </div>
              </div>
            )}
            
            {wizardStep === 2 && (
              <div className="space-y-4">
                <div className="relative mb-4">
                  <Input
                    label={`Buscar ${selectedEntityType === 'client' ? 'cliente' : 'proveedor'}`}
                    id="entitySearch"
                    name="entitySearch"
                    value={entitySearchQuery}
                    onChange={(e) => setEntitySearchQuery(e.target.value)}
                    placeholder={`Buscar por nombre o email...`}
                  />
                  <div className="absolute right-3 top-9">
                    <Search className="h-5 w-5 text-gray-400" />
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto">
                  {selectedEntityType === 'client' ? (
                    clients
                      .filter(client => 
                        !entitySearchQuery || 
                        client.name.toLowerCase().includes(entitySearchQuery.toLowerCase()) ||
                        (client.email && client.email.toLowerCase().includes(entitySearchQuery.toLowerCase()))
                      )
                      .map(client => (
                      <Button
                        key={client.id}
                        variant="outline"
                        className="text-left justify-start h-auto py-2"
                        onClick={() => handleSelectEntity(client.id, client.name)}
                      >
                        <div>
                          <p className="font-medium">{client.name}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {client.email || 'Sin email'} • {client.phone || 'Sin teléfono'}
                          </p>
                        </div>
                      </Button>
                    ))
                  ) : (
                    suppliers
                      .filter(supplier => 
                        !entitySearchQuery || 
                        supplier.name.toLowerCase().includes(entitySearchQuery.toLowerCase()) ||
                        (supplier.email && supplier.email.toLowerCase().includes(entitySearchQuery.toLowerCase()))
                      )
                      .map(supplier => (
                      <Button
                        key={supplier.id}
                        variant="outline"
                        className="text-left justify-start h-auto py-2"
                        onClick={() => handleSelectEntity(supplier.id, supplier.name)}
                      >
                        <div>
                          <p className="font-medium">{supplier.name}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {supplier.email || 'Sin email'} • {supplier.phone || 'Sin teléfono'}
                          </p>
                        </div>
                      </Button>
                    ))
                  )}
                </div>
                <div className="flex justify-between mt-4">
                  <Button
                    variant="outline"
                    onClick={() => setWizardStep(1)}
                  >
                    Atrás
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setIsWizardOpen(false)}
                  >
                    Cancelar
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      
      {/* Filters */}
      <Card>
        <CardHeader title="Filtros" />
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="relative">
              <Input
                label="Buscar"
                id="searchQuery"
                name="searchQuery"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar por número o entidad..."
              />
              <div className="absolute right-3 top-9">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
            </div>
            
            <Select
              label="Período"
              value={period}
              onChange={(value) => {
                setPeriod(value);
                if (value === 'custom') {
                  // Don't change dates for custom range
                } else if (value === 'all') {
                  setStartDate('');
                  setEndDate('');
                }
              }}
              options={[
                { value: 'all', label: 'Todo' },
                { value: 'today', label: 'Hoy' },
                { value: '7days', label: 'Últimos 7 días' },
                { value: 'month', label: 'Este mes' },
                { value: 'quarter', label: 'Este trimestre' },
                { value: 'year', label: 'Este año' },
                { value: 'custom', label: 'Personalizado' },
              ]}
            />
            
            <Select
              label="Tipo de Entidad"
              value={selectedEntityType}
              onChange={(value) => setSelectedEntityType(value as 'all' | 'client' | 'supplier')}
              options={[
                { value: 'all', label: 'Todas' },
                { value: 'client', label: 'Clientes' },
                { value: 'supplier', label: 'Proveedores' },
              ]}
            />
            
            <Select
              label="Tipo de Factura"
              value={selectedInvoiceType}
              onChange={(value) => setSelectedInvoiceType(value as 'all' | 'A' | 'B' | 'C' | 'X')}
              options={[
                { value: 'all', label: 'Todas' },
                { value: 'A', label: 'Factura A' },
                { value: 'B', label: 'Factura B' },
                { value: 'C', label: 'Factura C' },
                { value: 'X', label: 'Factura X' },
              ]}
            />
            
            {period === 'custom' && (
              <>
                <Input
                  type="date"
                  label="Desde"
                  id="startDate"
                  name="startDate"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
                
                <Input
                  type="date"
                  label="Hasta"
                  id="endDate"
                  name="endDate"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Invoices Table */}
      <Card>
        <CardHeader title={`Facturas (${sortedInvoices.length})`} />
        <CardContent>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Número
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Fecha
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Vencimiento
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Entidad
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Tipo
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Total
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Saldo
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                {sortedInvoices.length > 0 ? (
                  sortedInvoices.map((invoice) => (
                    <tr 
                      key={invoice.id} 
                      className={`hover:bg-gray-50 dark:hover:bg-gray-800 ${
                        invoice.voided ? 'bg-red-50 dark:bg-red-900/20' : ''
                      }`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                        {invoice.number}
                        {invoice.voided && (
                          <span className="ml-2 text-red-600 dark:text-red-400 text-xs">(Anulada)</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                          {formatArgentinaDate(invoice.date)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {invoice.due_date ? formatArgentinaDate(invoice.due_date) : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {getEntityName(invoice.entity_id, invoice.entity_type)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span
                          className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            invoice.entity_type === 'client'
                              ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
                              : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                          }`}
                        >
                          {invoice.entity_type === 'client' ? 'Cliente' : 'Proveedor'} - {invoice.type}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-right">
                        <span
                          className={
                            invoice.entity_type === 'client'
                              ? 'text-emerald-600 dark:text-emerald-400'
                              : 'text-red-600 dark:text-red-400'
                          }
                        >
                          {formatCurrency(invoice.total)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-right">
                        <span
                          className={
                            invoice.balance > 0
                              ? 'text-amber-600 dark:text-amber-400'
                              : 'text-green-600 dark:text-green-400'
                          }
                        >
                          {formatCurrency(invoice.balance)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                        <div className="flex justify-center space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            icon={<Eye className="h-4 w-4" />}
                            onClick={() => handleViewInvoice(invoice)}
                          >
                            Ver
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            icon={<Download className="h-4 w-4" />}
                            onClick={() => handleGeneratePDF(invoice)}
                          >
                            PDF
                          </Button>
                          {!invoice.voided && (
                            <Button
                              variant="outline"
                              size="sm"
                              icon={<Trash2 className="h-4 w-4" />}
                              onClick={() => handleDeleteInvoice(invoice.id)}
                            >
                              Eliminar
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                      No hay facturas que coincidan con los filtros seleccionados
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      
      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setInvoiceToDelete(null);
        }}
        onConfirm={confirmDeleteInvoice}
        title="Eliminar Factura"
        message="¿Estás seguro de que deseas eliminar esta factura? Esta acción no se puede deshacer."
        confirmText="Eliminar"
        variant="danger"
      />
      
      {/* Error Modal */}
      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        message={error || 'Ha ocurrido un error'}
      />
      
      {/* Invoice Modal */}
      {selectedEntityId && (
        <InvoiceModal
          isOpen={isInvoiceModalOpen}
          onClose={() => {
            setIsInvoiceModalOpen(false);
            setSelectedEntityId(null);
          }}
          entity_id={selectedEntityId}
          entity_type={selectedEntityType as 'client' | 'supplier'}
          entityName={selectedEntityName}
        />
      )}
      
      {/* Payment Modal */}
      {createdInvoiceId && (
        <PaymentModal
          isOpen={isPaymentModalOpen}
          onClose={handlePaymentModalClose}
          entityId={selectedEntityId || ''}
          entityName={selectedEntityName}
        />
      )}
    </div>
  );
};

export default Invoices;